import { FISHYMAP_BRIDGE_V2_VERSION } from "./map-bridge-contract.js";
import { normalizeStatePatch } from "./map-bridge-input.js";
import { parseIntegerParam } from "./map-runtime-settings.js";

const SNAPSHOT_KINDS = new Set(["bootstrap", "state", "telemetry", "performanceTrace"]);

const LAYER_STYLE_KEYS = Object.freeze([
  "layerOpacities",
  "layerHighlights",
  "layerConfidenceShowcase",
  "layerClipMasks",
  "layerWaypointConnectionsVisible",
  "layerWaypointLabelsVisible",
  "layerPointIconsVisible",
  "layerPointIconScales",
  "layerPointClusteringEnabled",
]);
const FILTER_KEYS = Object.freeze([
  "searchExpression",
  "layerFilterBindingIdsDisabledByLayer",
  "filterOnlyActiveConditionGroups",
  "lootConditionSelection",
  "lootConditionContext",
  "tradeNpcClassesVisible",
]);
const UI_KEYS = Object.freeze([
  "showPoints",
  "showPointIcons",
  "pointIconScale",
  "autoAdjustView",
  "threeDEnabled",
  "sampleFromFilteredElements",
  "effectsEnabled",
  "focusWavesEnabled",
  "sharedFishState",
  "hoverFactsVisibleByLayer",
  "bookmarkSelectedIds",
  "bookmarks",
]);

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOnlyKeys(value, keys) {
  const allowed = new Set(["kind", ...keys]);
  return Object.keys(value).every((key) => allowed.has(key));
}

function ownValues(value, keys) {
  return Object.fromEntries(
    keys.filter((key) => hasOwn(value, key)).map((key) => [key, value[key]]),
  );
}

function normalizeBranch(kind, branch, patch, keys) {
  if (!hasOnlyKeys(patch, keys)) {
    return null;
  }
  const normalized = normalizeStatePatch({
    [branch]: ownValues(patch, keys),
  })[branch];
  return normalized ? { kind, ...normalized } : null;
}

export function normalizeFishyMapV2Patch(patch = {}) {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
    return null;
  }
  switch (patch.kind) {
    case "theme":
      return normalizeBranch("theme", "theme", patch, ["name", "colors"]);
    case "layerVisibility":
      return normalizeBranch("layerVisibility", "filters", patch, ["layerIdsVisible"]);
    case "layerOrder":
      return normalizeBranch("layerOrder", "filters", patch, ["layerIdsOrdered"]);
    case "layerStyle":
      return normalizeBranch("layerStyle", "filters", patch, LAYER_STYLE_KEYS);
    case "filter":
      return normalizeBranch("filter", "filters", patch, FILTER_KEYS);
    case "ui":
      return normalizeBranch("ui", "ui", patch, UI_KEYS);
    case "settings":
      return normalizeBranch("settings", "settings", patch, ["memoryBudgetMiB"]);
    case "characterCatalog":
      return normalizeBranch("characterCatalog", "characterCatalog", patch, [
        "version",
        "revision",
        "characters",
      ]);
    case "characterPresence":
      return normalizeBranch("characterPresence", "characterPresence", patch, [
        "version",
        "revision",
        "characters",
      ]);
    default:
      return null;
  }
}

function finiteNumberOrUndefined(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function v2PatchCoalescingKey(patch) {
  return patch?.kind || null;
}

export function coalesceFishyMapV2Patches(patches = []) {
  const output = [];
  const indexByKey = new Map();
  for (const patch of patches) {
    const normalized = normalizeFishyMapV2Patch(patch);
    if (!normalized) {
      continue;
    }
    const key = v2PatchCoalescingKey(normalized);
    if (!key) {
      output.push(normalized);
      continue;
    }
    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, output.length);
      output.push(normalized);
      continue;
    }
    output[existingIndex] = normalized;
  }
  return output;
}

export function createFishyMapV2InputPatchBatch(patches = [], options = {}) {
  const sequence = Math.max(0, parseIntegerParam(options.sequence) ?? 0);
  const clientTimeMs = finiteNumberOrUndefined(options.clientTimeMs);
  return {
    version: FISHYMAP_BRIDGE_V2_VERSION,
    sequence,
    ...(clientTimeMs !== undefined ? { clientTimeMs } : {}),
    patches: coalesceFishyMapV2Patches(patches),
  };
}

export function normalizeFishyMapV2SnapshotKind(value) {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!SNAPSHOT_KINDS.has(normalized)) {
    throw new TypeError(`unsupported map snapshot kind: ${String(value)}`);
  }
  return normalized;
}

export function normalizeFishyMapV2SnapshotRequest(request = {}) {
  if (!isPlainObject(request)) {
    throw new TypeError("map snapshot request must be an object");
  }
  if (!hasOnlyKeys(request, ["requestId", "maxEstimatedBytes", "maxBuildMs"])) {
    throw new TypeError("map snapshot request contains unsupported fields");
  }
  const out = {
    version: FISHYMAP_BRIDGE_V2_VERSION,
    kind: normalizeFishyMapV2SnapshotKind(
      hasOwn(request, "kind") ? request.kind : "state",
    ),
  };
  if (hasOwn(request, "requestId")) {
    if (typeof request.requestId !== "string") {
      throw new TypeError("map snapshot requestId must be a string");
    }
    const requestId = request.requestId.trim();
    if (requestId) {
      out.requestId = requestId;
    }
  }
  if (hasOwn(request, "maxEstimatedBytes")) {
    if (
      !Number.isSafeInteger(request.maxEstimatedBytes) ||
      request.maxEstimatedBytes < 0
    ) {
      throw new TypeError("map snapshot maxEstimatedBytes must be a non-negative integer");
    }
    out.maxEstimatedBytes = request.maxEstimatedBytes;
  }
  if (hasOwn(request, "maxBuildMs")) {
    if (!Number.isFinite(request.maxBuildMs) || request.maxBuildMs < 0) {
      throw new TypeError("map snapshot maxBuildMs must be a non-negative number");
    }
    out.maxBuildMs = request.maxBuildMs;
  }
  return out;
}

export function statePatchToFishyMapV2Patches(patch = {}) {
  const normalized = normalizeStatePatch(patch);
  const patches = [];
  if (normalized.theme) {
    patches.push({
      kind: "theme",
      ...(normalized.theme.name ? { name: normalized.theme.name } : {}),
      ...(normalized.theme.colors ? { colors: normalized.theme.colors } : {}),
    });
  }
  if (normalized.filters) {
    const filters = normalized.filters;
    if (hasOwn(filters, "layerIdsVisible")) {
      patches.push({
        kind: "layerVisibility",
        layerIdsVisible: filters.layerIdsVisible,
      });
    }
    if (hasOwn(filters, "layerIdsOrdered")) {
      patches.push({
        kind: "layerOrder",
        layerIdsOrdered: filters.layerIdsOrdered,
      });
    }
    const stylePatch = { kind: "layerStyle" };
    for (const key of [
      "layerOpacities",
      "layerHighlights",
      "layerConfidenceShowcase",
      "layerClipMasks",
      "layerWaypointConnectionsVisible",
      "layerWaypointLabelsVisible",
      "layerPointIconsVisible",
      "layerPointIconScales",
      "layerPointClusteringEnabled",
    ]) {
      if (hasOwn(filters, key)) {
        stylePatch[key] = filters[key];
      }
    }
    if (Object.keys(stylePatch).length > 1) {
      patches.push(stylePatch);
    }
    const filterPatch = { kind: "filter" };
    for (const key of [
      "searchExpression",
      "layerFilterBindingIdsDisabledByLayer",
      "filterOnlyActiveConditionGroups",
      "lootConditionSelection",
      "lootConditionContext",
      "tradeNpcClassesVisible",
    ]) {
      if (hasOwn(filters, key)) {
        filterPatch[key] = filters[key];
      }
    }
    if (Object.keys(filterPatch).length > 1) {
      patches.push(filterPatch);
    }
  }
  if (normalized.ui) {
    const uiPatch = {
      kind: "ui",
      ...ownValues(normalized.ui, UI_KEYS),
    };
    if (Object.keys(uiPatch).length > 1) {
      patches.push(uiPatch);
    }
  }
  if (normalized.settings) {
    patches.push({
      kind: "settings",
      ...normalized.settings,
    });
  }
  if (normalized.characterCatalog) {
    patches.push({
      kind: "characterCatalog",
      ...normalized.characterCatalog,
    });
  }
  if (normalized.characterPresence) {
    patches.push({
      kind: "characterPresence",
      ...normalized.characterPresence,
    });
  }
  return coalesceFishyMapV2Patches(patches);
}

function fishyMapV2CommandEnvelope(command, options = {}) {
  return {
    version: FISHYMAP_BRIDGE_V2_VERSION,
    ...(options.commandId ? { commandId: options.commandId } : {}),
    command,
  };
}

export function commandsToFishyMapV2CommandEnvelopes(command = {}, options = {}) {
  const normalized = normalizeStatePatch({ commands: command }).commands;
  if (!normalized) {
    return [];
  }
  const commands = [];
  if (normalized.resetView === true) {
    commands.push({ kind: "resetView" });
  }
  if (normalized.focusSearch === true) {
    commands.push({ kind: "focusSearch" });
  }
  if (normalized.restoreView) {
    commands.push({ kind: "restoreView", view: normalized.restoreView });
  } else if (normalized.setViewMode === "2d" || normalized.setViewMode === "3d") {
    commands.push({ kind: "setViewMode", viewMode: normalized.setViewMode });
  }
  if (Number.isFinite(normalized.selectZoneRgb)) {
    commands.push({ kind: "selectZoneRgb", zoneRgb: normalized.selectZoneRgb });
  }
  if (normalized.selectSemanticField) {
    commands.push({
      kind: "selectSemanticField",
      ...normalized.selectSemanticField,
    });
  }
  if (normalized.selectWorldPoint) {
    commands.push({
      kind: "selectWorldPoint",
      ...normalized.selectWorldPoint,
    });
  }
  return commands.map((entry, index) =>
    fishyMapV2CommandEnvelope(entry, {
      commandId: options.commandIdPrefix ? `${options.commandIdPrefix}:${index + 1}` : undefined,
    }),
  );
}
